import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  Chip,
  Avatar,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  Card,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Grow,
  Badge,
} from '@mui/material';
import {
  Notifications,
  Business,
  Event,
  CheckCircle,
  Error,
  Warning,
  Info,
  FilterList,
  Clear,
  Refresh,
  ThumbUp,
  ThumbDown,
  Launch,
  Schedule,
  Person,
  Email,
  Phone,
} from '@mui/icons-material';
import axios from 'axios';
import { useSocket, addGlobalListener, removeGlobalListener } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { useCatAssistant } from '../contexts/CatAssistantContext';

interface Notification {
  id: string;
  type: 'reservationCreated' | 'reservationUpdated' | 'reservationDeleted';
  message: string;
  timestamp: Date;
  businessId: string;
  businessName: string;
  boardId: string;
  boardName: string;
  reservationId: string;
  status?: string;
  customerName?: string;
  isNew?: boolean;
}

interface Business {
  id: string;
  name: string;
}

interface Board {
  id: string;
  name: string;
  businessId: string;
}

const AdminInbox: React.FC = () => {
  console.log('AdminInbox: Component mounting');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filteredNotifications, setFilteredNotifications] = useState<Notification[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [boards, setBoards] = useState<Board[]>([]);
  const [filterBusiness, setFilterBusiness] = useState<string>('');
  const [filterBoard, setFilterBoard] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterDate, setFilterDate] = useState<string>('');
  const [error, setError] = useState('');
  const [selectedReservation, setSelectedReservation] = useState<any>(null);
  const [reservationDialogOpen, setReservationDialogOpen] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [loading, setLoading] = useState(true);

  const socket = useSocket();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { say } = useCatAssistant();
  const joinedRooms = useRef<Set<string>>(new Set());
  const pendingEvents = useRef<{ event: string, data: any }[]>([]);

  console.log('AdminInbox: socket available:', !!socket, 'user:', user);

  // Helper functions defined before use
  const saveNotificationsToStorage = (nots: Notification[]) => {
    localStorage.setItem('adminNotifications', JSON.stringify(nots));
  };

  const addNotification = useCallback((notification: Notification, isNew = false) => {
    setNotifications(prev => {
      // Check if notification with same reservationId and type already exists
      const exists = prev.some(n => n.reservationId === notification.reservationId && n.type === notification.type);
      if (exists) {
        console.log('Notification already exists, skipping duplicate');
        return prev;
      }

      const notificationWithFlag = { ...notification, isNew };
      const updated = [notificationWithFlag, ...prev].slice(0, 100); // Keep last 100
      saveNotificationsToStorage(updated);

      // Update global notification count for header badge
      const newCount = updated.filter(n => n.status === 'pending').length;
      localStorage.setItem('notificationCount', newCount.toString());
      // Trigger storage event for other components
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'notificationCount',
        newValue: newCount.toString()
      }));

      return updated;
    });

    // Remove isNew flag after animation
    if (isNew) {
      setTimeout(() => {
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, isNew: false } : n)
        );
      }, 3000); // 3 seconds animation
    }

    // Show browser notification if permission granted
    if (Notification.permission === 'granted') {
      new Notification('Nueva Reserva', {
        body: `${notification.customerName} reservó en ${notification.boardName}`,
        icon: '/favicon.ico',
        tag: `reservation-${notification.reservationId}`
      });
    }

    // Play notification sound
    try {
      // Short beep sound to avoid large file size issues
      const audio = new Audio('data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU');
      audio.play().catch(e => console.log('Audio play failed:', e));
    } catch (error) {
      console.log('Notification sound not supported');
    }
  }, []);

  const fetchInboxData = async () => {
    try {
      setLoading(true);
      console.log('AdminInbox: Fetching inbox data...');
      const response = await axios.get('/api/dashboard/inbox');
      const { businesses, boards, reservations } = response.data;

      console.log('AdminInbox: Data fetched:', { businesses: businesses.length, boards: boards.length, reservations: reservations.length });

      setBusinesses(businesses);
      setBoards(boards);

      // Process reservations into notifications
      const newNotifications: Notification[] = reservations.map((r: any) => {
        const board = boards.find((b: any) => b.id === r.boardId);
        return {
          id: `existing_${r.id}`,
          type: 'reservationCreated' as const,
          message: `Reserva pendiente: ${r.customerName} en ${board?.name}`,
          timestamp: new Date(r.createdAt),
          businessId: String(board?.businessId || 0),
          businessName: businesses.find((b: any) => b.id === board?.businessId)?.name || 'Negocio',
          boardId: String(r.boardId),
          boardName: board?.name || 'Agenda',
          reservationId: String(r.id),
          status: r.status,
          customerName: r.customerName
        };
      });
      setNotifications(newNotifications);
      saveNotificationsToStorage(newNotifications);

      // Initialize global notification count
      const pendingCount = newNotifications.filter(n => n.status === 'pending').length;
      localStorage.setItem('notificationCount', pendingCount.toString());
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'notificationCount',
        newValue: pendingCount.toString()
      }));

      // Process any pending socket events now that boards are loaded
      pendingEvents.current.forEach(({ event, data }) => handleSocketEvent(event, data, boards, businesses));
      pendingEvents.current = [];
    } catch (err: any) {
      console.error('AdminInbox: Error fetching inbox data:', err);
      setError('Error al cargar datos de la bandeja de entrada');
    } finally {
      setLoading(false);
    }
  };

  const handleSocketEvent = useCallback((event: string, data: any, overrideBoards?: Board[], overrideBusinesses?: Business[]) => {
    console.log('🔥 AdminInbox: Handling socket event:', event, data);

    const actualBoards = overrideBoards || boards;
    const actualBusinesses = overrideBusinesses || businesses;

    // Handle case where data might be an array
    const eventData = Array.isArray(data) ? data[0] : data;

    // If we don't have boards/businesses loaded yet, store for later processing
    if (actualBoards.length === 0) {
      console.log('🔥 AdminInbox: Boards not loaded yet, storing event for later');
      pendingEvents.current.push({ event, data: eventData });
      return;
    }

    if (event === 'reservationCreated') {
      const board = actualBoards.find(b => String(b.id) === String(eventData.boardId));
      if (board) {
        addNotification({
          id: `created_${Date.now()}`,
          type: 'reservationCreated',
          message: `Nueva reserva en ${board.name}: ${eventData.customerName}`,
          timestamp: new Date(),
          businessId: String(board.businessId),
          businessName: actualBusinesses.find(b => b.id === board.businessId)?.name || 'Negocio',
          boardId: String(board.id),
          boardName: board.name,
          reservationId: String(eventData.id),
          status: eventData.status,
          customerName: eventData.customerName
        }, true); // Mark as new for animation
      }
    } else if (event === 'reservationUpdated') {
      const board = actualBoards.find(b => String(b.id) === String(eventData.boardId));
      if (board) {
        addNotification({
          id: `updated_${Date.now()}`,
          type: 'reservationUpdated',
          message: `Reserva actualizada en ${board.name}: ${eventData.customerName}`,
          timestamp: new Date(),
          businessId: String(board.businessId),
          businessName: actualBusinesses.find(b => b.id === board.businessId)?.name || 'Negocio',
          boardId: String(board.id),
          boardName: board.name,
          reservationId: String(eventData.id),
          status: eventData.status
        });
      }
    } else if (event === 'reservationDeleted') {
      const board = actualBoards.find(b => String(b.id) === String(eventData.boardId));
      if (board) {
        addNotification({
          id: `deleted_${Date.now()}`,
          type: 'reservationDeleted',
          message: `Reserva eliminada en ${board.name}`,
          timestamp: new Date(),
          businessId: String(board.businessId),
          businessName: actualBusinesses.find(b => b.id === board.businessId)?.name || 'Negocio',
          boardId: String(board.id),
          boardName: board.name,
          reservationId: String(eventData.id)
        });
      }
    } else if (event === 'reservationRestored') {
      const board = actualBoards.find(b => String(b.id) === String(eventData.boardId));
      if (board) {
        addNotification({
          id: `restored_${Date.now()}`,
          type: 'reservationUpdated',
          message: `Reserva restaurada en ${board.name}: ${eventData.customerName}`,
          timestamp: new Date(),
          businessId: String(board.businessId),
          businessName: actualBusinesses.find(b => b.id === board.businessId)?.name || 'Negocio',
          boardId: String(board.id),
          boardName: board.name,
          reservationId: String(eventData.id),
          status: eventData.status,
          customerName: eventData.customerName
        });
      }
    }
  }, [boards, businesses, addNotification]);

  const setupSocketListeners = useCallback(() => {
    if (!socket) return;

    // Join all board rooms
    boards.forEach(board => {
      if (!joinedRooms.current.has(board.id)) {
        console.log('Joining board:', board.id, 'Type:', typeof board.id);
        socket.emit('joinBoard', String(board.id)); // Ensure string
        joinedRooms.current.add(board.id);
      }
    });
  }, [socket, boards]);

  const applyFilters = useCallback(() => {
    let filtered = notifications;

    if (filterBusiness) {
      filtered = filtered.filter(n => n.businessId === filterBusiness);
    }
    if (filterBoard) {
      filtered = filtered.filter(n => n.boardId === filterBoard);
    }
    if (filterStatus) {
      filtered = filtered.filter(n => n.status === filterStatus);
    }
    if (filterDate) {
      const filterDateObj = new Date(filterDate);
      filtered = filtered.filter(n =>
        n.timestamp.toDateString() === filterDateObj.toDateString()
      );
    }

    setFilteredNotifications(filtered);
  }, [notifications, filterBusiness, filterBoard, filterStatus, filterDate]);

  // Ref to always have the latest handleSocketEvent in the stable listener
  const handlerRef = useRef(handleSocketEvent);
  useEffect(() => {
    handlerRef.current = handleSocketEvent;
  }, [handleSocketEvent]);

  useEffect(() => {
    // Clear old notifications on mount to prevent accumulation
    localStorage.removeItem('adminNotifications');
    setNotifications([]);

    fetchInboxData();

    // Register global notification callback using the ref
    const handleGlobalNotification = (event: string, data: any) => {
      console.log('🔥 AdminInbox: Global notification received (via ref):', event, data);
      handlerRef.current(event, data);
    };

    addGlobalListener(handleGlobalNotification);

    return () => {
      // Clear global callback when component unmounts
      removeGlobalListener(handleGlobalNotification);
    };
  }, []); // Empty dependency array - listener is stable via ref

  useEffect(() => {
    // console.log('AdminInbox: useEffect triggered, boards.length:', boards.length, 'socket:', socket);
    if (boards.length > 0 && socket) {
      setupSocketListeners();
    }
    // No cleanup needed for listeners since we don't add them directly anymore
  }, [boards, socket, setupSocketListeners]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Handlers
  const clearFilters = () => {
    setFilterBusiness('');
    setFilterBoard('');
    setFilterStatus('');
    setFilterDate('');
  };

  const handleViewReservation = async (notification: Notification) => {
    try {
      const response = await axios.get(`/api/reservations/${notification.reservationId}`);
      setSelectedReservation({ ...response.data, notification });
      setReservationDialogOpen(true);
    } catch (err: any) {
      console.error('Error loading reservation details:', err);
      setError('Error al cargar los detalles de la reserva');
    }
  };

  const handleApproveReservation = async (reservationId: string) => {
    setLoadingAction(true);
    try {
      await axios.put(`/api/reservations/${reservationId}`, { status: 'confirmed' });
      setReservationDialogOpen(false);
      setSelectedReservation(null);
      // Update notification status
      setNotifications(prev => {
        const updated = prev.map(n =>
          n.reservationId === reservationId ? { ...n, status: 'confirmed' } : n
        );
        saveNotificationsToStorage(updated);

        // Update global notification count (remove from pending count)
        const newCount = updated.filter(n => n.status === 'pending').length;
        localStorage.setItem('notificationCount', newCount.toString());
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'notificationCount',
          newValue: newCount.toString()
        }));

        return updated;
      });
      say('¡Reserva aprobada! 🎊', 'celebration');
    } catch (err: any) {
      console.error('Error approving reservation:', err);
      setError('Error al aprobar la reserva');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleRejectReservation = async (reservationId: string) => {
    setLoadingAction(true);
    try {
      await axios.put(`/api/reservations/${reservationId}`, { status: 'cancelled' });
      setReservationDialogOpen(false);
      setSelectedReservation(null);
      // Update notification status
      setNotifications(prev => {
        const updated = prev.map(n =>
          n.reservationId === reservationId ? { ...n, status: 'cancelled' } : n
        );
        saveNotificationsToStorage(updated);

        // Update global notification count (remove from pending count)
        const newCount = updated.filter(n => n.status === 'pending').length;
        localStorage.setItem('notificationCount', newCount.toString());
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'notificationCount',
          newValue: newCount.toString()
        }));

        return updated;
      });
      say('Reserva rechazada 😔', 'alert');
    } catch (err: any) {
      console.error('Error rejecting reservation:', err);
      setError('Error al rechazar la reserva');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleNavigateToBoard = (boardId: string) => {
    navigate(`/board/${boardId}`);
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'reservationCreated': return <CheckCircle color="success" />;
      case 'reservationUpdated': return <Info color="info" />;
      case 'reservationDeleted': return <Error color="error" />;
      default: return <Notifications />;
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Notifications sx={{ fontSize: 40, color: 'primary.main' }} />
          Bandeja de Administrador
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Recibe notificaciones en tiempo real de todas tus reservas
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Filters */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList />
            Filtros
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Negocio</InputLabel>
              <Select
                value={filterBusiness}
                onChange={(e) => setFilterBusiness(e.target.value)}
                label="Negocio"
              >
                <MenuItem value="">Todos</MenuItem>
                {businesses.map(business => (
                  <MenuItem key={business.id} value={business.id}>
                    {business.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Agenda</InputLabel>
              <Select
                value={filterBoard}
                onChange={(e) => setFilterBoard(e.target.value)}
                label="Agenda"
              >
                <MenuItem value="">Todos</MenuItem>
                {boards.filter(board => !filterBusiness || board.businessId === filterBusiness).map(board => (
                  <MenuItem key={board.id} value={board.id}>
                    {board.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Estado</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Estado"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="pending">Pendiente</MenuItem>
                <MenuItem value="confirmed">Confirmada</MenuItem>
                <MenuItem value="cancelled">Cancelada</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              type="date"
              label="Fecha"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />

            <Button variant="outlined" onClick={clearFilters} startIcon={<Clear />}>
              Limpiar
            </Button>
            <Button variant="contained" onClick={fetchInboxData} startIcon={<Refresh />} disabled={loading}>
              Actualizar
            </Button>
          </Box>
        </Paper>

        {/* Notifications List */}
        <Paper elevation={2} sx={{ borderRadius: 2 }}>
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Badge
                badgeContent={filteredNotifications.filter(n => n.isNew).length}
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    animation: filteredNotifications.some(n => n.isNew) ? 'pulse 1s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': { transform: 'scale(1)' },
                      '50%': { transform: 'scale(1.2)' },
                      '100%': { transform: 'scale(1)' },
                    },
                  },
                }}
              >
                Notificaciones
              </Badge>
              ({filteredNotifications.length})
            </Typography>
          </Box>
          {filteredNotifications.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Notifications sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No hay notificaciones
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Las nuevas actividades aparecerán aquí automáticamente
              </Typography>
            </Box>
          ) : (
            <List sx={{ py: 0 }}>
              {filteredNotifications.map((notification, index) => (
                <Grow in={true} key={notification.id} timeout={notification.isNew ? 600 : 0}>
                  <div>
                    <ListItem
                      sx={{
                        py: 2,
                        px: 3,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        ...(notification.isNew && {
                          bgcolor: 'primary.light',
                          animation: 'highlight 3s ease-out',
                          '@keyframes highlight': {
                            '0%': { bgcolor: 'primary.light', transform: 'scale(1.02)' },
                            '50%': { bgcolor: 'primary.light', transform: 'scale(1.02)' },
                            '100%': { bgcolor: 'transparent', transform: 'scale(1)' },
                          },
                        })
                      }}
                      onClick={() => handleViewReservation(notification)}
                    >
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.light' }}>
                        {getNotificationIcon(notification.type)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {notification.message}
                          </Typography>
                          {notification.status && (
                            <Chip
                              label={notification.status === 'pending' ? 'Pendiente' :
                                notification.status === 'confirmed' ? 'Confirmada' :
                                  notification.status === 'cancelled' ? 'Cancelada' : notification.status}
                              size="small"
                              color={getStatusColor(notification.status) as any}
                            />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {notification.businessName} • {notification.boardName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {notification.timestamp.toLocaleString('es-ES')}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNavigateToBoard(notification.boardId);
                              }}
                              sx={{ color: 'primary.main' }}
                              title="Ir al tablero"
                            >
                              <Launch fontSize="small" />
                            </IconButton>
                            {notification.status === 'pending' && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleApproveReservation(notification.reservationId);
                                  }}
                                  sx={{ color: 'success.main' }}
                                  title="Aprobar"
                                >
                                  <ThumbUp fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRejectReservation(notification.reservationId);
                                  }}
                                  sx={{ color: 'error.main' }}
                                  title="Rechazar"
                                >
                                  <ThumbDown fontSize="small" />
                                </IconButton>
                              </>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </ListItem>
                    {index < filteredNotifications.length - 1 && <Divider />}
                  </div>
                </Grow>
              ))}
            </List>
          )}
        </Paper>

        {/* Reservation Details Dialog */}
        <Dialog
          open={reservationDialogOpen}
          onClose={() => setReservationDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Person color="primary" />
            Detalles de la Reserva
          </DialogTitle>
          <DialogContent>
            {selectedReservation && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  <Card sx={{ p: 2, flex: '1 1 300px' }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person />
                      Información del Cliente
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography><strong>Nombre:</strong> {selectedReservation.customerName}</Typography>
                      <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Email fontSize="small" />
                        <strong>Email:</strong> {selectedReservation.customerEmail}
                      </Typography>
                      {selectedReservation.customerPhone && (
                        <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone fontSize="small" />
                          <strong>Teléfono:</strong> {selectedReservation.customerPhone}
                        </Typography>
                      )}
                    </Box>
                  </Card>
                  <Card sx={{ p: 2, flex: '1 1 300px' }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Schedule />
                      Detalles de la Reserva
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography><strong>Desde:</strong> {new Date(selectedReservation.startDate).toLocaleString('es-ES')}</Typography>
                      <Typography><strong>Hasta:</strong> {new Date(selectedReservation.endDate).toLocaleString('es-ES')}</Typography>
                      <Typography><strong>Estado:</strong>
                        <Chip
                          label={selectedReservation.status === 'pending' ? 'Pendiente' :
                            selectedReservation.status === 'confirmed' ? 'Confirmada' :
                              selectedReservation.status === 'cancelled' ? 'Cancelada' : selectedReservation.status}
                          size="small"
                          color={getStatusColor(selectedReservation.status) as any}
                          sx={{ ml: 1 }}
                        />
                      </Typography>
                      {selectedReservation.paymentInfo && (
                        <Typography><strong>Pago:</strong> {selectedReservation.paymentInfo}</Typography>
                      )}
                    </Box>
                  </Card>
                </Box>
                <Card sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Información de la Agenda
                  </Typography>
                  <Typography><strong>Negocio:</strong> {selectedReservation.notification.businessName}</Typography>
                  <Typography><strong>Agenda:</strong> {selectedReservation.notification.boardName}</Typography>
                  <Typography><strong>Notificación:</strong> {selectedReservation.notification.timestamp.toLocaleString('es-ES')}</Typography>
                </Card>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 2 }}>
            <Button
              onClick={() => setReservationDialogOpen(false)}
              variant="outlined"
            >
              Cerrar
            </Button>
            <Button
              onClick={() => handleNavigateToBoard(selectedReservation?.notification.boardId)}
              variant="outlined"
              startIcon={<Launch />}
            >
              Ir a la Agenda
            </Button>
            {selectedReservation?.status === 'pending' && (
              <>
                <Button
                  onClick={() => handleApproveReservation(selectedReservation.id)}
                  variant="contained"
                  color="success"
                  startIcon={loadingAction ? <CircularProgress size={20} /> : <ThumbUp />}
                  disabled={loadingAction}
                >
                  {loadingAction ? 'Procesando...' : 'Aprobar Reserva'}
                </Button>
                <Button
                  onClick={() => handleRejectReservation(selectedReservation.id)}
                  variant="contained"
                  color="error"
                  startIcon={loadingAction ? <CircularProgress size={20} /> : <ThumbDown />}
                  disabled={loadingAction}
                >
                  {loadingAction ? 'Procesando...' : 'Rechazar Reserva'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default AdminInbox;